import {
  DASHBOARD_CONFERENCE_HISTORY,
  DASHBOARD_POST_GAME_SEASON,
  DASHBOARD_STATCREW,
} from '../constants/routes';
import {
  DashboardApiResponse,
  DashboardDetailItem,
  DashboardDetailSection,
  DashboardModuleKey,
  DashboardModuleView,
} from '../types/dashboard';

type ModuleConfig = {
  title: string;
  route: string;
  aliases: string[];
  defaultSubtitle: string;
  defaultDescription: string;
  emptyHelperText: string;
};

const MODULE_CONFIGS: Record<DashboardModuleKey, ModuleConfig> = {
  statCrew: {
    title: 'StatCrew',
    route: DASHBOARD_STATCREW,
    aliases: ['statCrew', 'stat_crew', 'stewCrew', 'stew_crew', 'stat crew'],
    defaultSubtitle: 'Season ingestion',
    defaultDescription: 'Season XML ingestion, mapping, and verification.',
    emptyHelperText: 'No StatCrew data available for the selected sport.',
  },
  conferenceHistory: {
    title: 'Conference History',
    route: DASHBOARD_CONFERENCE_HISTORY,
    aliases: ['conferenceHistory', 'conference_history', 'conference history'],
    defaultSubtitle: 'Continuous Process',
    defaultDescription: 'Conference affiliations by season.',
    emptyHelperText: 'Conference History is empty for the selected sport.',
  },
  postGameSeason: {
    title: 'Post-season Game',
    route: DASHBOARD_POST_GAME_SEASON,
    aliases: ['postGameSeason', 'post_game_season', 'postSeasonGame', 'post season game'],
    defaultSubtitle: 'Continuous Process',
    defaultDescription: 'Game-level tagging for post-season tournaments.',
    emptyHelperText: 'Post-season Game is empty for the selected sport.',
  },
};

function toRecord(value: unknown): Record<string, unknown> | null {
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }

  return null;
}

function normalizeLookupKey(value: string) {
  return value.replace(/[\s_-]+/g, '').toLowerCase();
}

function formatLabel(value: string) {
  return value
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function stringifyValue(value: unknown): string {
  if (value === null || value === undefined || value === '') {
    return 'Not provided';
  }

  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  if (Array.isArray(value)) {
    return value.map((item) => stringifyValue(item)).join(', ');
  }

  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return String(value);
}

function findModuleData(
  source: DashboardApiResponse | Record<string, unknown> | null,
  aliases: string[]
): unknown {
  if (!source) {
    return null;
  }

  const normalizedAliases = aliases.map(normalizeLookupKey);

  for (const [key, value] of Object.entries(source)) {
    if (normalizedAliases.includes(normalizeLookupKey(key))) {
      return value;
    }
  }

  return null;
}

function getModuleRawData(
  dashboardData: DashboardApiResponse | null,
  moduleKey: DashboardModuleKey
): unknown {
  if (!dashboardData) {
    return null;
  }

  const config = MODULE_CONFIGS[moduleKey];
  const nestedData = toRecord(dashboardData.data);
  const nestedModules = toRecord(dashboardData.modules);
  const sources = [dashboardData, nestedData, nestedModules];

  for (const source of sources) {
    const rawModule = findModuleData(source, config.aliases);

    if (rawModule !== null && rawModule !== undefined) {
      return rawModule;
    }
  }

  return null;
}

function unwrapModuleValue(value: unknown): unknown {
  const record = toRecord(value);

  if (!record) {
    return value;
  }

  const nestedKeys = ['data', 'items', 'records', 'details', 'results'];

  for (const key of nestedKeys) {
    if (key in record && hasModuleData(record[key])) {
      return record[key];
    }
  }

  return value;
}

function hasModuleData(value: unknown): boolean {
  const unwrappedValue = unwrapModuleValue(value);

  if (Array.isArray(unwrappedValue)) {
    return unwrappedValue.length > 0;
  }

  if (typeof unwrappedValue === 'string') {
    return unwrappedValue.trim().length > 0;
  }

  if (typeof unwrappedValue === 'number' || typeof unwrappedValue === 'boolean') {
    return true;
  }

  const record = toRecord(unwrappedValue);

  if (!record) {
    return false;
  }

  return Object.values(record).some((entry) => {
    if (Array.isArray(entry)) {
      return entry.length > 0;
    }

    if (typeof entry === 'object' && entry !== null) {
      return Object.keys(entry as Record<string, unknown>).length > 0;
    }

    if (typeof entry === 'string') {
      return entry.trim().length > 0;
    }

    return Boolean(entry);
  });
}

function pickText(record: Record<string, unknown> | null, keys: string[]) {
  if (!record) {
    return '';
  }

  for (const key of keys) {
    const value = record[key];

    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }

  return '';
}

function pickNumber(record: Record<string, unknown> | null, keys: string[]) {
  if (!record) {
    return undefined;
  }

  for (const key of keys) {
    const value = record[key];

    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === 'string' && value.trim() && !Number.isNaN(Number(value))) {
      return Number(value);
    }
  }

  return undefined;
}

function getSummaryLine(
  moduleKey: DashboardModuleKey,
  rawData: unknown,
  available: boolean
) {
  if (!available) {
    return MODULE_CONFIGS[moduleKey].emptyHelperText;
  }

  const unwrappedValue = unwrapModuleValue(rawData);
  const record = toRecord(unwrappedValue) || toRecord(rawData);

  const seasonsUsed = pickNumber(record, ['seasonsUsed', 'seasons_used']);
  const totalSeasons = pickNumber(record, ['totalSeasons', 'total_seasons']);

  if (seasonsUsed !== undefined && totalSeasons !== undefined) {
    return `${seasonsUsed} / ${totalSeasons} Seasons Used`;
  }

  const pendingReview = pickNumber(record, ['pendingReview', 'pending_review']);

  if (pendingReview !== undefined) {
    return `${pendingReview} Seasons Pending Review`;
  }

  const count = pickNumber(record, ['count', 'total', 'recordsCount', 'records_count']);

  if (count !== undefined) {
    return `${count} Records Available`;
  }

  if (Array.isArray(unwrappedValue)) {
    return `${unwrappedValue.length} Records Available`;
  }

  const detailText = pickText(record, ['summaryLine', 'summary', 'message', 'title']);

  if (detailText) {
    return detailText;
  }

  return 'Data retrieved successfully';
}

function getHelperText(rawData: unknown, available: boolean, emptyText: string) {
  if (!available) {
    return emptyText;
  }

  const unwrappedValue = unwrapModuleValue(rawData);
  const record = toRecord(unwrappedValue) || toRecord(rawData);
  const helperText = pickText(record, ['helperText', 'footerText', 'note', 'message']);

  if (helperText) {
    return helperText;
  }

  if (Array.isArray(unwrappedValue)) {
    return `${unwrappedValue.length} records returned from the dashboard API.`;
  }

  return 'Click to open this module page.';
}

function getStatusLabel(rawData: unknown, available: boolean) {
  const record = toRecord(unwrapModuleValue(rawData)) || toRecord(rawData);
  const statusText = pickText(record, ['statusLabel', 'status', 'stage']);

  if (statusText) {
    return statusText.toUpperCase();
  }

  return available ? 'IN-PROGRESS' : 'NO DATA';
}

function getSubtitle(moduleKey: DashboardModuleKey, rawData: unknown) {
  const record = toRecord(unwrapModuleValue(rawData)) || toRecord(rawData);

  return (
    pickText(record, ['subtitle', 'processType', 'processName', 'type']) ||
    MODULE_CONFIGS[moduleKey].defaultSubtitle
  );
}

function getDescription(moduleKey: DashboardModuleKey, rawData: unknown) {
  const record = toRecord(unwrapModuleValue(rawData)) || toRecord(rawData);

  return (
    pickText(record, ['description', 'summary', 'details']) ||
    MODULE_CONFIGS[moduleKey].defaultDescription
  );
}

export function buildDashboardModules(
  dashboardData: DashboardApiResponse | null
): Record<DashboardModuleKey, DashboardModuleView> {
  return (Object.keys(MODULE_CONFIGS) as DashboardModuleKey[]).reduce(
    (result, moduleKey) => {
      const config = MODULE_CONFIGS[moduleKey];
      const rawData = getModuleRawData(dashboardData, moduleKey);
      const available = hasModuleData(rawData);

      result[moduleKey] = {
        key: moduleKey,
        title: config.title,
        route: config.route,
        available,
        statusLabel: getStatusLabel(rawData, available),
        summaryLine: getSummaryLine(moduleKey, rawData, available),
        subtitle: getSubtitle(moduleKey, rawData),
        description: getDescription(moduleKey, rawData),
        helperText: getHelperText(rawData, available, config.emptyHelperText),
        rawData,
      };

      return result;
    },
    {} as Record<DashboardModuleKey, DashboardModuleView>
  );
}

function toItemRows(entry: unknown): DashboardDetailItem[] {
  const record = toRecord(entry);

  if (!record) {
    return [
      {
        label: 'Value',
        value: stringifyValue(entry),
      },
    ];
  }

  return Object.entries(record)
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .slice(0, 8)
    .map(([key, value]) => ({
      label: formatLabel(key),
      value: stringifyValue(value),
    }));
}

export function buildModuleSections(rawData: unknown): DashboardDetailSection[] {
  const unwrappedValue = unwrapModuleValue(rawData);

  if (Array.isArray(unwrappedValue)) {
    return unwrappedValue.slice(0, 6).map((entry, index) => ({
      title: `Record ${index + 1}`,
      items: toItemRows(entry),
    }));
  }

  const record = toRecord(unwrappedValue);

  if (!record) {
    return [
      {
        title: 'Details',
        items: toItemRows(unwrappedValue),
      },
    ];
  }

  const scalarItems = Object.entries(record)
    .filter(([, value]) => !Array.isArray(value) && !(typeof value === 'object' && value !== null))
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .slice(0, 8)
    .map(([key, value]) => ({
      label: formatLabel(key),
      value: stringifyValue(value),
    }));

  const sections: DashboardDetailSection[] = [];

  if (scalarItems.length > 0) {
    sections.push({
      title: 'Overview',
      items: scalarItems,
    });
  }

  const nestedEntries = Object.entries(record).filter(
    (entry): entry is [string, unknown[]] => Array.isArray(entry[1])
  );

  if (nestedEntries.length > 0) {
    const [firstKey, firstValue] = nestedEntries[0];

    sections.push(
      ...firstValue.slice(0, 5).map((entry, index) => ({
        title: `${formatLabel(firstKey)} ${index + 1}`,
        items: toItemRows(entry),
      }))
    );
  }

  if (sections.length > 0) {
    return sections;
  }

  return [
    {
      title: 'Details',
      items: toItemRows(record),
    },
  ];
}
